import React, { useState, useEffect } from "react";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { useRef } from "react";
import { confirmPopup } from "primereact/confirmpopup";
import adminService from "../../services/admin/admin.services";
import { CreateVideoDto, VideoDto } from "../../services/admin/admin.dto";
import { useToast } from "../../utils/toast";
import { ConfirmPopup } from "primereact/confirmpopup";
import EditPresidentMessageComponent from "./EditPresidentMessageComponent";
import EditReportsComponent from "./EditReportsComponent";

const VIDEO_TYPES = [
  { label: "Bülten", value: "BULTEN" },
  { label: "Eğitim", value: "EGITIM" },
  { label: "İş Sürekliliği", value: "IS_SUREKLILIGI" }
];

const PageEdit = () => {
  const toast = useToast();
  const [activeIndex, setActiveIndex] = useState(0);
  const [bultenVideos, setBultenVideos] = useState<VideoDto[]>([]);
  const [egitimVideos, setEgitimVideos] = useState<VideoDto[]>([]);
  const [isSurekliligiVideos, setIsSurekliligiVideos] = useState<VideoDto[]>([]);
  const [displayVideoDialog, setDisplayVideoDialog] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<VideoDto | null>(null);
  const [currentVideoType, setCurrentVideoType] = useState("BULTEN");
  const [newVideo, setNewVideo] = useState<CreateVideoDto>({
    title: "",
    video_url: "",
    video_type: "BULTEN"
  });

  useEffect(() => {
    loadAllVideos();
  }, []);

  const loadAllVideos = async () => {
    try {
      const [bultenResponse, egitimResponse, isSurekliligiResponse] = await Promise.all([
        adminService.getVideos("BULTEN"),
        adminService.getVideos("EGITIM"),
        adminService.getVideos("IS_SUREKLILIGI")
      ]);

      // Bülten videoları
      if (bultenResponse.result?.data) {
        setBultenVideos(bultenResponse?.result?.data || []);
      } else {
        toast.show(`Bülten videoları yüklenirken hata: ${bultenResponse?.result?.error}`, "error");
      }

      // Eğitim videoları
      if (egitimResponse?.result?.data) {
        setEgitimVideos(egitimResponse?.result?.data || []);
      } else {
        toast.show(`Eğitim videoları yüklenirken hata: ${egitimResponse?.result?.error}`, "error");
      }

      // İş sürekliliği videoları
      if (isSurekliligiResponse?.result?.data) {
        setIsSurekliligiVideos(isSurekliligiResponse?.result?.data || []);
      } else {
        toast.show(`İş sürekliliği videoları yüklenirken hata: ${isSurekliligiResponse?.result?.error}`, "error");
      }
    } catch (error: any) {
      console.error("Error loading videos:", error);
      let errorMessage = "Videolar yüklenirken bir hata oluştu.";

      if (error.message === "Network Error") {
        errorMessage =
          "Sunucuya bağlanılamıyor. Lütfen internet bağlantınızı kontrol edin veya daha sonra tekrar deneyin.";
      } else if (error.response) {
        // Backend'den gelen hata mesajı varsa
        errorMessage = `Sunucu hatası: ${error.response.data?.error || error.response.statusText}`;
      } else if (error.request) {
        // İstek yapıldı ama cevap alınamadı
        errorMessage = "Sunucudan cevap alınamıyor. Lütfen daha sonra tekrar deneyin.";
      }

      toast.show(errorMessage, "error");

      // Hata durumunda boş array set et
      setBultenVideos([]);
      setEgitimVideos([]);
      setIsSurekliligiVideos([]);
    }
  };

  const handleVideoSave = async () => {
    try {
      const videoTitle = selectedVideo?.title || newVideo.title;

      // Başlık uzunluğu kontrolü
      if (newVideo.title.length > 200) {
        toast.show("Video başlığı 200 karakterden uzun olamaz", "error");
        return;
      }

      // URL uzunluğu kontrolü
      if (newVideo.video_url.length > 500) {
        toast.show("Video URL'si 500 karakterden uzun olamaz", "error");
        return;
      }

      let response;
      if (selectedVideo?.id) {
        response = await adminService.updateVideo(selectedVideo.id, newVideo);
      } else {
        response = await adminService.createVideo(newVideo);
      }
      if (response?.result?.data) {
        setDisplayVideoDialog(false);
        loadAllVideos();
        toast.show(`Video ${videoTitle} başarıyla ${selectedVideo?.title ? "güncellendi" : "eklendi"}`, "success");
      } else {
        toast.show(response?.result?.error || "Video kaydedilirken bir hata oluştu", "error");
      }
    } catch (error) {
      console.error("Error saving video:", error);
      toast.show(`Video kaydedilirken bir hata oluştu: ${error}`, "error");
    }
  };

  const handleVideoDelete = async (id: number, title: string, event: React.MouseEvent<HTMLButtonElement>) => {
    confirmPopup({
      target: event.currentTarget,
      message: `"${title}" başlıklı videoyu silmek istediğinize emin misiniz?`,
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Evet",
      rejectLabel: "Hayır",
      accept: async () => {
        try {
          const response = await adminService.deleteVideo(id);
          if (!response.error) {
            toast.show("Video başarıyla silindi", "success");
            loadAllVideos();
          } else {
            toast.show(response.error.errors[0] || "Video silinirken bir hata oluştu", "error");
          }
        } catch (error) {
          console.error("Error deleting video:", error);
          toast.show(`Video silinirken bir hata oluştu: ${error}`, "error");
        }
      },
      reject: () => {}
    });
  };

  const videoActionBodyTemplate = (rowData: VideoDto) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-pencil"
          rounded
          outlined
          className="mr-2"
          onClick={() => {
            setSelectedVideo(rowData);
            setNewVideo(rowData);
            setDisplayVideoDialog(true);
          }}
        />
        <Button
          icon="pi pi-trash"
          rounded
          outlined
          severity="danger"
          onClick={(e) => handleVideoDelete(rowData.id!, rowData.title, e)}
        />
      </div>
    );
  };

  const handleAddNewVideo = (type: string) => {
    setCurrentVideoType(type);
    setSelectedVideo(null);
    setNewVideo({
      title: "",
      video_url: "",
      video_type: type
    });
    setDisplayVideoDialog(true);
  };

  const renderVideoTable = (videos: VideoDto[], type: string) => {
    return (
      <>
        <div className="flex justify-content-end mb-3">
          <Button label="Yeni Video Ekle" icon="pi pi-plus" onClick={() => handleAddNewVideo(type)} />
        </div>

        <DataTable value={videos} paginator rows={10} dataKey="id">
          <Column field="title" header="Başlık" sortable />
          <Column field="video_url" header="Video URL" />
          <Column
  field="created_at"
  header="Eklenme Tarihi"
  sortable
  body={(rowData) =>
    new Date(rowData.created_at).toLocaleDateString("tr-TR")
  }
/>
          <Column body={videoActionBodyTemplate} exportable={false} style={{ minWidth: "12rem" }} />
        </DataTable>
      </>
    );
  };

  return (
    <>
      <ConfirmPopup />
      <div className="card">
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel header="Bülten Videoları">{renderVideoTable(bultenVideos, "BULTEN")}</TabPanel>

          <TabPanel header="Eğitim Videoları">{renderVideoTable(egitimVideos, "EGITIM")}</TabPanel>

          <TabPanel header="İş Sürekliliği Videoları">
            {renderVideoTable(isSurekliligiVideos, "IS_SUREKLILIGI")}
          </TabPanel>
          <TabPanel header="Başkan Mesajı">
            <EditPresidentMessageComponent />
          </TabPanel>
          <TabPanel header="Raporlar">
            <EditReportsComponent />
          </TabPanel>
        </TabView>

        <Dialog
          visible={displayVideoDialog}
          style={{ width: "50vw" }}
          header={selectedVideo ? "Video Düzenle" : "Yeni Video"}
          modal
          className="p-fluid"
          onHide={() => setDisplayVideoDialog(false)}
        >
          <div className="field">
            <label htmlFor="edit_title">Başlık</label>
            <InputText
              id="edit_title"
              value={newVideo.title}
              onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
              maxLength={200}
            />
          </div>

          <div className="field">
            <label htmlFor="edit_video_url">Video URL</label>
            <InputText
              id="edit_video_url"
              value={newVideo.video_url}
              onChange={(e) => setNewVideo({ ...newVideo, video_url: e.target.value })}
              maxLength={500}
            />
          </div>

          <div className="flex justify-content-end mt-4">
            <Button
              label="İptal"
              icon="pi pi-times"
              outlined
              onClick={() => setDisplayVideoDialog(false)}
              className="mr-2"
            />
            <Button label="Kaydet" icon="pi pi-check" onClick={handleVideoSave} />
          </div>
        </Dialog>
      </div>
    </>
  );
};

export default PageEdit;
